import { useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { eventService } from '../../../services/event.service';
import { tagService } from '../../../services/tags.service';
import { PERMISSIONS } from '../../../shared/permission';
import PermissionChecker from '../../UI/PermissionChecker/PermissionChecker';
import Button from '../../UI/Button/Button';
import Input from '../../UI/Input/Input';
import Select from '../../UI/Select/Select';
import MultiSelectAutocomplete from '../../UI/MultiSelectAutocomplete/MultiSelectAutocomplete';
import Alert from '../../UI/Alert/Alert';
import { toast } from 'react-toastify';
import styles from './EventEditForm.module.css';

const toDateInput = (isoString) => {
    if (!isoString) return '';
    return new Date(isoString).toISOString().slice(0, 10);
};

const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
const MAX_FILE_SIZE = 10 * 1024 * 1024;

const validateUrl = (url) => {
    if (!url) return true;
    try { new URL(url); return true; } catch { return false; }
};

const emptyState = () => ({ value: '', error: null });

function EventEditForm({ isOpen, onClose, onSuccess, eventId }) {
    const { t } = useTranslation('common');
    const { t: tValidation } = useTranslation('validation');
    const fileInputRef = useRef(null);

    const [event, setEvent] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [fetchError, setFetchError] = useState(null);
    const [tagsList, setTagsList] = useState([]);

    const [title, setTitle] = useState(emptyState());
    const [description, setDescription] = useState(emptyState());
    const [organizerLink, setOrganizerLink] = useState(emptyState());
    const [startDate, setStartDate] = useState(emptyState());
    const [endDate, setEndDate] = useState(emptyState());
    const [status, setStatus] = useState(emptyState());
    const [tags, setTags] = useState({ value: [], error: null });
    const [file, setFile] = useState({ value: null, error: null });
    const [generalError, setGeneralError] = useState(null);

    const statusOptions = [
        { value: 'draft', label: t('events.status_draft') },
        { value: 'published', label: t('events.status_published') },
        { value: 'archived', label: t('events.status_archived') },
    ];

    useEffect(() => {
        if (!isOpen || !eventId) return;
        let alive = true;

        tagService.getTagsAll()
            .then((data) => { if (alive) setTagsList(data?.tags ?? data ?? []); })
            .catch(() => {});

        const load = async () => {
            setIsLoading(true);
            setFetchError(null);
            try {
                const data = await eventService.getEventById(eventId);
                const ev = data?.event ?? data;
                if (alive) {
                    setEvent(ev);
                    setTitle({ value: ev.title ?? '', error: null });
                    setDescription({ value: ev.description ?? '', error: null });
                    setOrganizerLink({ value: ev.organizer_link ?? '', error: null });
                    setStartDate({ value: toDateInput(ev.start_date), error: null });
                    setEndDate({ value: toDateInput(ev.end_date), error: null });
                    setStatus({ value: ev.status ?? 'draft', error: null });
                    setTags({ value: ev.tags ?? [], error: null });
                    setFile({ value: null, error: null });
                }
            } catch (err) {
                if (alive) {
                    const errorKey = err?.payload?.error;
                    setFetchError(typeof errorKey === 'string' ? errorKey : 'network_error');
                }
            } finally {
                if (alive) setIsLoading(false);
            }
        };
        load();
        return () => { alive = false; };
    }, [isOpen, eventId]);

    if (!isOpen) return null;

    const handleClose = () => {
        setGeneralError(null);
        setFetchError(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
        onClose();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setGeneralError(null);

        if (organizerLink.value && !validateUrl(organizerLink.value)) {
            setOrganizerLink((prev) => ({ ...prev, error: { code: 'format.url' } }));
            return;
        }

        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (endDate.value && new Date(endDate.value) < today) {
            setEndDate((prev) => ({ ...prev, error: { code: 'end_date_cannot_be_in_the_past' } }));
            return;
        }

        setIsSaving(true);
        try {
            const formData = new FormData();
            formData.append('id', String(eventId));
            formData.append('title', title.value);
            formData.append('description', description.value);
            formData.append('start_date', startDate.value);
            formData.append('end_date', endDate.value);
            formData.append('status', status.value);
            if (organizerLink.value) formData.append('organizer_link', organizerLink.value);
            if (file.value) formData.append('file', file.value);
            const tagsArray = tags.value.map((tag) => tag?.code ?? tag?.value ?? tag);
            if (tagsArray.length > 0) formData.append('tags', JSON.stringify(tagsArray));

            await eventService.updateEvent(formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            toast.success(tValidation('event_updated_successfully'));
            onSuccess?.();
            handleClose();
        } catch (error) {
            const errorEntity = error?.payload?.error;
            if (errorEntity && Array.isArray(errorEntity)) {
                errorEntity.forEach((errorItem) => {
                    const field = errorItem.path?.[0];
                    if (field === 'title') setTitle((prev) => ({ ...prev, error: errorItem }));
                    if (field === 'description') setDescription((prev) => ({ ...prev, error: errorItem }));
                    if (field === 'organizer_link') setOrganizerLink((prev) => ({ ...prev, error: errorItem }));
                    if (field === 'start_date') setStartDate((prev) => ({ ...prev, error: errorItem }));
                    if (field === 'end_date') setEndDate((prev) => ({ ...prev, error: errorItem }));
                    if (field === 'status') setStatus((prev) => ({ ...prev, error: errorItem }));
                });
            } else if (typeof errorEntity === 'string') {
                const message = tValidation(errorEntity);
                setGeneralError(message);
                toast.error(message);
            } else {
                toast.error(tValidation('something_went_wrong'));
            }
        } finally {
            setIsSaving(false);
        }
    };

    const isBusy = isSaving;

    return (
        <PermissionChecker permissions={[PERMISSIONS.EVENTS.EDIT]}>
            <div className={styles.overlay}>
                <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
                    <div className={styles.modalHeader}>
                        <h2 className={styles.title}>{t('events.edit')}</h2>
                        <button
                            type="button"
                            className={styles.closeButton}
                            onClick={handleClose}
                            disabled={isBusy}
                        >
                            ×
                        </button>
                    </div>

                    {isLoading && (
                        <div className={styles.loadingState}>
                            {t('loading', 'Loading...')}
                        </div>
                    )}

                    {!isLoading && fetchError && (
                        <Alert type="error">
                            <p>{tValidation(fetchError)}</p>
                        </Alert>
                    )}

                    {!isLoading && !fetchError && (
                        <>
                            {generalError && (
                                <Alert type="error">
                                    <p>{generalError}</p>
                                </Alert>
                            )}

                            <form id="event-edit-form" className={styles.form} onSubmit={handleSubmit}>
                                <div className={styles.formGroup}>
                                    <label className={styles.label}>{t('events.title_label')} *</label>
                                    <Input
                                        type="text"
                                        placeholder={t('events.title_placeholder')}
                                        value={title.value}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            setTitle({ value: val, error: val.length > 200 ? { code: 'title_too_long', params: { maximum: 200 } } : null });
                                        }}
                                        error={title.error}
                                        disabled={isBusy}
                                    />
                                    <div className={`${styles.charCount} ${title.value.length > 200 ? styles.charCountError : ''}`}>
                                        {title.value.length} / 200
                                    </div>
                                </div>

                                <div className={styles.formGroup}>
                                    <label className={styles.label}>{t('events.description_label')} *</label>
                                    <textarea
                                        className={`${styles.textarea} ${description.error ? styles.textareaError : ''}`}
                                        placeholder={t('events.description_placeholder')}
                                        value={description.value}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            setDescription({ value: val, error: val.length > 5000 ? { code: 'description_too_long', params: { maximum: 5000 } } : null });
                                        }}
                                        rows={5}
                                        disabled={isBusy}
                                    />
                                    <div className={`${styles.charCount} ${description.value.length > 5000 ? styles.charCountError : ''}`}>
                                        {description.value.length} / 5000
                                    </div>
                                    {description.error && (
                                        <p className={styles.errorText}>
                                            {/string\.min|string\.invalid/.test(description.error?.code ?? '')
                                                ? tValidation('field_required')
                                                : tValidation(description.error?.code || '').replace('{{toReplace}}', description.error?.params?.maximum ?? description.error?.params?.minimum)}
                                        </p>
                                    )}
                                </div>

                                <div className={styles.formGroup}>
                                    <label className={styles.label}>{t('events.organizer_link_label')}</label>
                                    <Input
                                        type="url"
                                        placeholder={t('events.organizer_link_placeholder')}
                                        value={organizerLink.value}
                                        onChange={(e) => setOrganizerLink({ value: e.target.value, error: null })}
                                        error={organizerLink.error}
                                        disabled={isBusy}
                                    />
                                </div>

                                <div className={styles.formGroup}>
                                    <label className={styles.label}>{t('events.status_label')}</label>
                                    <Select
                                        options={statusOptions}
                                        value={status.value}
                                        onChange={(e) => setStatus({ value: e.target.value, error: null })}
                                        error={status.error}
                                        disabled={isBusy}
                                    />
                                </div>

                                <div className={styles.row}>
                                    <div className={styles.formGroup}>
                                        <label className={styles.label}>{t('events.start_date_label')} *</label>
                                        <Input
                                            type="date"
                                            value={startDate.value}
                                            onChange={(e) => setStartDate({ value: e.target.value, error: null })}
                                            error={startDate.error}
                                            disabled={isBusy}
                                        />
                                    </div>
                                    <div className={styles.formGroup}>
                                        <label className={styles.label}>{t('events.end_date_label')} *</label>
                                        <Input
                                            type="date"
                                            value={endDate.value}
                                            onChange={(e) => setEndDate({ value: e.target.value, error: null })}
                                            error={endDate.error}
                                            disabled={isBusy}
                                        />
                                    </div>
                                </div>

                                <div className={styles.formGroup}>
                                    <label className={styles.label}>{t('events.cover_label')}</label>
                                    <div className={styles.fileInputWrapper}>
                                        <input
                                            ref={fileInputRef}
                                            id="event-edit-cover-file"
                                            type="file"
                                            className={styles.fileInputHidden}
                                            onChange={(e) => {
                                                const f = e.target.files?.[0] ?? null;
                                                if (f) {
                                                    if (!ALLOWED_IMAGE_TYPES.includes(f.type)) {
                                                        setFile({ value: null, error: { code: 'file_type_invalid' } });
                                                        e.target.value = '';
                                                        return;
                                                    }
                                                    if (f.size > MAX_FILE_SIZE) {
                                                        setFile({ value: null, error: { code: 'file_size_exceeded' } });
                                                        e.target.value = '';
                                                        return;
                                                    }
                                                }
                                                setFile({ value: f, error: null });
                                            }}
                                            accept={ALLOWED_IMAGE_TYPES.join(',')}
                                            disabled={isBusy}
                                        />
                                        <span className={styles.fileName}>
                                            {file.value?.name || (event?.cover_image ? t('events.cover_current') : t('events.cover_placeholder'))}
                                        </span>
                                        <label className={styles.fileButton} htmlFor="event-edit-cover-file">
                                            {t('events.cover_button')}
                                        </label>
                                    </div>
                                    <p className={styles.hint}>{t('events.cover_hint')}</p>
                                    {file.error && (
                                        <p className={styles.errorText}>{tValidation(file.error?.code || '')}</p>
                                    )}
                                </div>

                                <div className={styles.formGroup}>
                                    <label className={styles.label}>{t('events.tags_label')}</label>
                                    <MultiSelectAutocomplete
                                        options={tagsList}
                                        value={tags.value}
                                        onChange={(values) => setTags({ value: values, error: null })}
                                        placeholder={t('ideas.tags_placeholder')}
                                        error={tags.error}
                                        disabled={isBusy}
                                    />
                                </div>

                                <div className={styles.buttons}>
                                    <Button variant="secondary" type="button" onClick={handleClose} disabled={isBusy}>
                                        {t('events.cancel')}
                                    </Button>
                                    <Button
                                        variant="primary"
                                        type="submit"
                                        disabled={isBusy || !!title.error || !!description.error}
                                    >
                                        {t('events.save')}
                                    </Button>
                                </div>
                            </form>
                        </>
                    )}
                </div>
            </div>
        </PermissionChecker>
    );
}

export default EventEditForm;
